import { paymentMethodLabel, reversalPresentation } from "@/services/paymentPresentation.js";
import { downloadAfriMedDocumentPdf, generateAfriMedDocumentPdf, printAfriMedDocumentPdf } from "@/services/referralDocumentService.js";

function text(value, fallback = "—") {
  const valueText = String(value || "").trim();
  return valueText || fallback;
}

function currency(amount, currencyCode, locale = "en-GB") {
  const amountNumber = Number(amount);
  const currency = String(currencyCode || "ZAR").toUpperCase();
  if (!Number.isFinite(amountNumber)) return "—";
  try {
    return new Intl.NumberFormat(locale, { style: "currency", currency }).format(amountNumber);
  } catch {
    return `${currency} ${amountNumber.toFixed(2)}`;
  }
}

function dateTime(value, locale = "en-GB") {
  const date = new Date(value || "");
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat(locale, {
    day: "2-digit", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
  }).format(date);
}


export function financialDocumentDetails(row = {}, kind = "appointment", options = {}) {
  const t = options?.t || ((_, fallback) => fallback);
  const locale = options?.languageCode || "en-GB";
  const reversal = row?.reversal || null;
  const reversalState = reversalPresentation(reversal || {}, t);
  const appointment = row?.appointment || {};
  const isRefund = kind === "refund";
  const amount = isRefund && reversal
    ? Number(reversal.amount_minor || 0) / 100
    : row?.amount;
  const reference = isRefund
    ? reversal?.provider_reversal_reference || reversal?.id
    : row?.reference || row?.id;
  return {
    kind: isRefund ? "refund" : "appointment",
    title: isRefund
      ? t("payment.documents.refundReceipt", "Refund receipt")
      : t("payment.documents.appointmentReceipt", "Appointment receipt"),
    fileName: `afrimed-${isRefund ? "refund" : "appointment"}-receipt-${String(reference || row?.id || "document").replace(/[^a-zA-Z0-9_-]+/g, "-")}.pdf`,
    reference: text(reference),
    paymentReference: text(row?.reference || row?.id),
    appointment: appointment?.appointment_date ? dateTime(appointment.appointment_date, locale) : "—",
    service: text(appointment?.booked_service_name || (String(row?.type || "").toLowerCase() === "pharmacy"
      ? t("payment.documents.pharmacyOrder", "Pharmacy order")
      : t("payment.documents.consultationFallback", "Appointment consultation"))),
    clinician: text(row?.clinician_name, t("payment.documents.clinicianFallback", "AfriMed clinician")),
    patient: text(row?.patient_name, t("payment.documents.patientFallback", "Patient")),
    consultation: String(appointment?.consultation_type || "").toLowerCase() === "private"
      ? t("payment.documents.virtual", "Virtual")
      : t("payment.documents.consultation", "Consultation"),
    paymentMethod: paymentMethodLabel(row, t),
    paymentState: isRefund
      ? (reversalState.label || t("payment.reversal.processing", "Refund processing"))
      : text(row?.status),
    amount: currency(amount, reversal?.currency || row?.currency, locale),
    refundDestination: isRefund ? reversalState.destination : "",
  };
}

export async function generateAppointmentFinancialPdf(row = {}, kind = "appointment", options = {}) {
  const t = options?.t || ((_, fallback) => fallback);
  const locale = options?.languageCode || "en-GB";
  const details = financialDocumentDetails(row, kind, options);

  const identityRows = [
    [t("payment.documents.date", "Date"), dateTime(row?.paid_at || row?.created_at, locale)],
    [t("payment.documents.receiptReference", "Receipt reference"), details.reference],
    ...(details.kind === "refund" ? [[t("payment.documents.originalPaymentReference", "Original payment reference"), details.paymentReference]] : []),
    [t("payment.documents.appointment", "Appointment"), details.appointment],
    [t("payment.documents.service", "Service"), details.service],
    [t("payment.documents.patient", "Patient"), details.patient],
    [t("payment.documents.clinician", "Clinician"), details.clinician],
    [t("payment.documents.consultation", "Consultation"), details.consultation],
  ];
  const financialRows = [
    [t("payment.documents.paymentMethod", "Payment method"), details.paymentMethod],
    [details.kind === "refund" ? t("payment.documents.refundStatus", "Refund status") : t("payment.documents.paymentStatus", "Payment status"), details.paymentState],
    [t("payment.documents.amount", "Amount"), details.amount],
    ...(details.refundDestination ? [[t("payment.documents.refundDestination", "Refund destination"), details.refundDestination]] : []),
  ];
  return { blob: await generateAfriMedDocumentPdf({ title: details.title, sections: [
    { title: t("payment.documents.appointmentDetails", "Appointment details"), rows: identityRows },
    { title: details.kind === "refund" ? t("payment.documents.refundSummary", "Refund summary") : t("payment.documents.paymentSummary", "Payment summary"), rows: financialRows },
  ] }), details };
}

export async function downloadAppointmentFinancialPdf(row = {}, kind = "appointment", options = {}) {
  const { blob, details } = await generateAppointmentFinancialPdf(row, kind, options);
  downloadAfriMedDocumentPdf(blob, details.fileName);
  return details;
}

export async function printAppointmentFinancialPdf(row = {}, kind = "appointment", options = {}) {
  const { blob } = await generateAppointmentFinancialPdf(row, kind, options);
  return printAfriMedDocumentPdf(blob);
}
